import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid, Legend } from 'recharts';
import axios from 'axios';
import { BASE_URL } from '../../config/axios';
import { getOrdinal, MonthRangePicker, CustomTooltip } from '../CustomFunctions';
import { StarPerformerIcon, InfoIcon } from '../AllSvg';
import Loader from '../Loader';
import dayjs from "dayjs";
import '../../style/StarPerformance.css'

const StarIcon = ({ fillPercentage, id }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id={`grad-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset={`${fillPercentage}%`} stopColor="#FFC107" />
        <stop offset={`${fillPercentage}%`} stopColor="#f0eded" />
      </linearGradient>
    </defs>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill={`url(#grad-${id})`} stroke="" strokeWidth="1" />
  </svg>
);

// Renders 5 stars with partial fill based on finalMarks / maxMarks
const renderStars = (finalMarks, maxMarks, idPrefix, size = 20) => {
  // 1. Calculate exact proportional score out of 5
  const exactScoreOutOf5 = Math.min(Number(finalMarks || 0) / (maxMarks / 5), 5);

  // 2. Round UP to nearest 0.5 step
  const scoreOutOf5 = Math.ceil(exactScoreOutOf5 * 2) / 2;

  return (
    <div style={{ display: "flex", gap: "2px", alignItems: "center", justifyContent: "center" }}>
      {[1, 2, 3, 4, 5].map((index) => {
        let fill = 0;

        // Full star
        if (scoreOutOf5 >= index) {
          fill = 100;
        }
        // Half star
        else if (scoreOutOf5 > index - 1) {
          fill = 50;
        }

        return (
          <StarIcon
            key={index}
            id={`${idPrefix}-${index}`}
            fillPercentage={fill}
            size={size}
          />
        );
      })}
    </div>
  );
};

const AppraisalMonthlyTracker = ({ customWidth = 'auto', customHeight = 'auto' }) => {
  const [activeTab, setActiveTab] = useState('combined'); // 'combined'
  const [view, setView] = useState('overview'); // 'overview' | 'graph' (only for attendance)
  const [modalView, setModalView] = useState(null); // 'attendance' | 'letters' | null

  const navigate = useNavigate();
  const location = useLocation();

  // Calculate Last Month
  const CIVILMANTRA_ID = "025d0b52-e892-4868-a3bb-6b70dcdffb4d";
  const companyId = sessionStorage.getItem("company_id");

  let defaultYear, defaultMonth;
  const today = new Date();

  if (companyId === CIVILMANTRA_ID) {
    let m = today.getMonth() + 1;
    let y = today.getFullYear();
    const isCycleActive = today >= new Date(2026, 5, 25);
    if (isCycleActive && today.getDate() >= 25) {
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }
    // "Last month" relative to payroll cycle
    defaultMonth = m - 1;
    defaultYear = y;
    if (defaultMonth === 0) {
      defaultMonth = 12;
      defaultYear -= 1;
    }
  } else {
    // Standard calendar last month
    const calDate = new Date();
    calDate.setMonth(calDate.getMonth() - 1);
    defaultYear = calDate.getFullYear();
    defaultMonth = calDate.getMonth() + 1;
  }

  const lastMonthStr = defaultMonth.toString().padStart(2, '0');
  const lastMonthFirstDay = `${defaultYear}-${lastMonthStr}-01`;
  const lastMonthLastDayNum = new Date(defaultYear, defaultMonth, 0).getDate();
  const lastMonthLastDay = `${defaultYear}-${lastMonthStr}-${lastMonthLastDayNum}`;

  const [startdate, setStartDate] = useState(lastMonthFirstDay);
  const [enddate, setEndDate] = useState(lastMonthLastDay);

  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [letterApiData, setLetterApiData] = useState(null);
  const [letterLoading, setLetterLoading] = useState(true);

  const [overallApiData, setOverallApiData] = useState(null);
  const [overallLoading, setOverallLoading] = useState(true);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        if (!startdate || !enddate || startdate === "null" || enddate === "null") return;

        setLoading(true);
        setLetterLoading(true);
        setOverallLoading(true);
        const empCode = sessionStorage.getItem("emp_code") || "CIPL10036";

        // Fetch Attendance
        try {
          const attUrl = `${BASE_URL}/wfm/star-performance-attendance-rating-employee-wise/?start_month_year=${startdate.slice(0, 7)}&end_month_year=${enddate.slice(0, 7)}&empcode=${empCode}`;
          const attResponse = await axios.get(attUrl);
          setApiData(attResponse.data);
        } catch (err) {
          console.error("Error fetching attendance rating:", err);
        } finally {
          setLoading(false);
        }

        // Fetch Letters
        try {
          const letterUrl = `${BASE_URL}/wfm/star-performance-letter-rating-employee-wise/?start_month_year=${startdate.slice(0, 7)}&end_month_year=${enddate.slice(0, 7)}&empcode=${empCode}`;
          const letterResponse = await axios.get(letterUrl);
          setLetterApiData(letterResponse.data);
        } catch (err) {
          console.error("Error fetching letter rating:", err);
        } finally {
          setLetterLoading(false);
        }

        // Fetch Overall
        try {
          const subCompany = sessionStorage.getItem("company_id") || "";
          const overallUrl = `${BASE_URL}/wfm/star-performance-overall-rating/?sub_company=${subCompany}&start_month_year=${startdate.slice(0, 7)}&end_month_year=${enddate.slice(0, 7)}&empcode=${empCode}`;
          const overallResponse = await axios.get(overallUrl);
          setOverallApiData(overallResponse.data);
        } catch (err) {
          console.error("Error fetching overall rating:", err);
        } finally {
          setOverallLoading(false);
        }

      } catch (err) {
        console.error("Error in fetchAllData:", err);
      }
    };

    fetchAllData();
  }, [startdate, enddate]);

  // Loading State
  if ((loading || letterLoading || overallLoading) && (!apiData && !letterApiData && !overallApiData)) {
    return (
      <div className="piechartt" style={{
        background: '#ffffff', borderRadius: '14px', padding: '20px', boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.06)', border: '1px solid #f1f5f9', minHeight: '450px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: customWidth,
        height: customHeight,
      }}>
        <Loader container={true} />
        {/* <div style={{ color: '#64748b', fontSize: '14px', fontWeight: '500' }}>Loading Star Performance Data...</div> */}
      </div>
    );
  }

  const goToRanking = () => {
    // "/ed/empDashboard" -> "ed", "/hr/hrDashboard" -> "hr", "/admin/..." -> "admin"
    const base = location.pathname.split('/').filter(Boolean)[0];

    navigate(`/${base}/userdetail`, {
      state: {
        toggleuser: 10,
        performanceTab: 3,
        startMonth: startdate.slice(0, 7),
        endMonth: enddate.slice(0, 7),
      },
    });
  };

  // --- ATTENDANCE DATA PREP ---
  const data = apiData || {
    attendance_rating: {
      total_marks: "10.00", final_marks: "0.00", total_deduction: "0.00",
      absent: { count: 0, deduction: "0.00" },
      late: { count: 0, deduction: "0.00" },
      halfday: { count: 0, deduction: "0.00" }
    },
    date_range: { start_date: startdate, end_date: enddate },
    department_name: "-", department_rank: 0,
    sub_company_name: "-", sub_company_rank: 0,
    monthly_late_details: []
  };

  const attendanceRating = data.attendance_rating || {};
  const absent = attendanceRating.absent || { count: 0, deduction: "0.00" };
  const late = attendanceRating.late || { count: 0, deduction: "0.00" };
  const halfday = attendanceRating.halfday || { count: 0, deduction: "0.00" };
  const monthlyLateDetails = data.monthly_late_details || [];

  // --- LETTER DATA PREP ---
  const letterData = letterApiData || {
    config: { total_marks: 20 },
    letter_rating: { default_marks: "10.00", marks_added: "0.00", marks_deducted: "0.00", final_marks: "0.00", letter_count: 0 },
    letters: []
  };
  const lRating = letterData.letter_rating || {};
  const lConfig = letterData.config || { total_marks: 20 };
  const lettersList = letterData.letters || [];

  return (
    <div className="piechartt dashboard-star-performance-card" style={{
      background: '#ffffff',
      borderRadius: '20px',
      padding: '20px',
      boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.06)',
      border: '1px solid #f1f5f9',
      fontFamily: '"Inter", sans-serif',
      width: customWidth,
      height: customHeight,
    }}>

      {/* Header & Tabs */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div style={{ width: '100%' }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#eef2ff",
                  color: "#2576bc",
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                  <path d="M9 16l2 2 4-4"></path>
                </svg>
              </div>
              <h2 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#0f172a' }}>
                Star Performance
              </h2>
            </div>
            {/* Date Pickers */}
            <div>
              <MonthRangePicker
                startDate={startdate}
                endDate={enddate}
                placeHolder1="Start Month"
                placeHolder2="End Month"
                maxMonth={dayjs().add(1, "month")}
                onChange={(start, end) => {
                  setStartDate(start || "null");
                  setEndDate(end || "null");
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Elegant Pill Tabs removed */}


          </div>
        </div>

        {/* Toggle Switch (Only for Attendance) */}
        {/*
        {modalView === 'attendance' && (
          <div style={{ display: 'flex', background: '#e2e8f0', borderRadius: '8px', padding: '4px', alignItems: 'center' }}>
            <button
              onClick={() => setView('overview')}
              title="Cards View"
              style={{
                background: view === 'overview' ? '#ffffff' : 'transparent',
                color: view === 'overview' ? '#0f172a' : '#64748b',
                boxShadow: view === 'overview' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                border: 'none', borderRadius: '6px', padding: '6px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s'
              }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"></rect><rect x="14" y="3" width="7" height="7" rx="1"></rect><rect x="14" y="14" width="7" height="7" rx="1"></rect><rect x="3" y="14" width="7" height="7" rx="1"></rect></svg>
            </button>
            <div style={{ width: '1px', height: '14px', background: '#cbd5e1', margin: '0 2px' }}></div>
            <button
              onClick={() => setView('graph')}
              title="Graph View"
              style={{
                background: view === 'graph' ? '#ffffff' : 'transparent',
                color: view === 'graph' ? '#0f172a' : '#64748b',
                boxShadow: view === 'graph' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                border: 'none', borderRadius: '6px', padding: '6px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s'
              }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 3 3 21 21 21"></polyline><polyline points="19 9 13 15 9 11 3 17"></polyline></svg>
            </button>
          </div>
        )}
        */}
      </div>

      {/* --- MODAL FOR FACTORS --- */}
      {modalView && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setModalView(null)}>
          <div className="sar-modal" onClick={e => e.stopPropagation()}>
            <button onClick={() => setModalView(null)} style={{ position: 'absolute', top: '16px', right: '16px', background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: '#0f172a' }}>
              {modalView === 'attendance' ? 'Attendance Factor Details' : 'Letter Factor Details'}
            </h2>
            <hr className="line" />
            {/* --- ATTENDANCE TAB CONTENT --- */}
            {modalView === 'attendance' && (
              view === 'overview' ? (
                <>
                  {/* Metrics Row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', padding: '0 10px' }}>

                    {/* Dept Rank */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                      <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px', color: '#0369a1', boxShadow: '0 4px 10px rgba(186,230,253,0.4)' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><line x1="12" y1="22" x2="12" y2="2"></line><line x1="8" y1="6" x2="8" y2="6"></line><line x1="8" y1="10" x2="8" y2="10"></line><line x1="8" y1="14" x2="8" y2="14"></line><line x1="8" y1="18" x2="8" y2="18"></line><line x1="16" y1="6" x2="16" y2="6"></line><line x1="16" y1="10" x2="16" y2="10"></line><line x1="16" y1="14" x2="16" y2="14"></line><line x1="16" y1="18" x2="16" y2="18"></line></svg>
                      </div>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: '2px 0' }}>{getOrdinal(data.department_rank)}</div>
                      <div style={{ fontSize: '14px', color: '#0f172a', fontWeight: '600' }}>Department Rank</div>
                      <div style={{ fontSize: '11px', color: '#475569', textAlign: 'center' }}>({data.department_name})</div>
                    </div>

                    {/* Final Score */}
                    <div style={{ position: 'relative', width: '130px', height: '130px', flexShrink: 0, margin: '0 20px' }}>
                      <div style={{ position: 'absolute', top: '10px', left: '10px', right: '10px', bottom: '10px', borderRadius: '50%', boxShadow: '0 4px 14px rgba(0,0,0,0.08)', background: '#ffffff', zIndex: 0 }}></div>
                      <svg width="130" height="130" viewBox="0 0 130 130" style={{ position: 'relative', zIndex: 1 }}>
                        <circle cx="65" cy="65" r="54" fill="none" stroke="#f1f5f9" strokeWidth="8" />
                        <circle cx="65" cy="65" r="54" fill="none" stroke="#3b82f6" strokeWidth="8"
                          strokeLinecap="round"
                          strokeDasharray="339.29"
                          strokeDashoffset={339.29 - ((parseFloat(attendanceRating.final_marks) / parseFloat(attendanceRating.total_marks)) * 339.29)}
                          transform="rotate(-90 65 65)"
                        />
                      </svg>
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                        <div style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', lineHeight: '1' }}>{attendanceRating.final_marks}</div>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginTop: '2px', marginBottom: '4px' }}>/ {parseInt(attendanceRating.total_marks)}</div>
                        <div style={{ fontSize: '10px', color: '#0f172a', fontWeight: '700', textTransform: 'uppercase' }}>Final Score</div>
                      </div>
                    </div>

                    {/* Company Rank */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                      <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'linear-gradient(135deg, #ffedd5 0%, #fed7aa 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px', color: '#c2410c', boxShadow: '0 4px 10px rgba(254,215,170,0.4)' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path><path d="M4 22h16"></path><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path></svg>
                      </div>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: '2px 0' }}>{getOrdinal(data.sub_company_rank)}</div>
                      <div style={{ fontSize: '14px', color: '#0f172a', fontWeight: '600' }}>Company Rank</div>
                      <div style={{ fontSize: '11px', color: '#475569', textAlign: 'center' }}>({data.sub_company_name})</div>
                    </div>

                  </div>

                  <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '0 0 20px 0' }} />

                  {/* Deductions Breakdown */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b' }}>Deductions Breakdown</span>
                      <div style={{ background: '#dc2626', color: '#ffffff', padding: '6px 14px', borderRadius: '20px', fontSize: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 2px 6px rgba(220,38,38,0.2)' }}>
                        Total: -{attendanceRating.total_deduction}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {/* Absent */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px', borderBottom: '1px solid #e2e8f0', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <div style={{ width: '34px', height: '34px', borderRadius: '14px', backgroundColor: '#fed7aa', color: '#c2410c', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line><line x1="9" y1="16" x2="15" y2="16"></line><line x1="12" y1="13" x2="12" y2="19"></line></svg>
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              Absent
                              <span title={absent.count > 0 ? `${(parseFloat(absent.deduction) / absent.count).toFixed(2)} marks deducted per absent day` : "No deductions"} style={{ cursor: 'help', color: '#94a3b8', display: 'inline-flex' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                              </span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px' }}>{absent.count} days</div>
                          </div>
                        </div>
                        <div style={{ fontWeight: '600', color: '#475569', fontSize: '14px' }}>-{absent.deduction}</div>
                      </div>

                      {/* Late */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px', borderBottom: '1px solid #e2e8f0', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <div style={{ width: '34px', height: '34px', borderRadius: '14px', backgroundColor: '#fef08a', color: '#b45309', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              Late
                              <span title={late.count > 0 ? `Deducted based on slab rules.` : "No deductions"} style={{ cursor: 'help', color: '#94a3b8', display: 'inline-flex' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                              </span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px' }}>{late.count} times</div>
                          </div>
                        </div>
                        <div style={{ fontWeight: '600', color: '#475569', fontSize: '14px' }}>-{late.deduction}</div>
                      </div>

                      {/* Half day */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <div style={{ width: '34px', height: '34px', borderRadius: '14px', backgroundColor: '#a7f3d0', color: '#047857', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 2v20"></path></svg>
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              Half day
                              <span title={halfday.count > 0 ? `${(parseFloat(halfday.deduction) / halfday.count).toFixed(2)} marks deducted per half day` : "No deductions"} style={{ cursor: 'help', color: '#94a3b8', display: 'inline-flex' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                              </span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px' }}>{halfday.count} days</div>
                          </div>
                        </div>
                        <div style={{ fontWeight: '600', color: '#475569', fontSize: '14px' }}>-{halfday.deduction}</div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* Graph View */
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b' }}>Monthly Deductions</span>
                  </div>

                  <div style={{ flex: 1, width: '100%', height: '410px' }}>
                    {monthlyLateDetails.length > 0 ? (
                      <ResponsiveContainer width="100%" height={410}>
                        <BarChart
                          data={monthlyLateDetails.reduce((acc, curr) => {
                            const existing = acc.find(item => item.month === curr.month);
                            const count = (curr.late_count || 0) + (curr.absent_count || 0) + (curr.halfday_count || 0);
                            const deduction = parseFloat(curr.deduction || 0);
                            if (existing) {
                              existing.count += count;
                              existing.deduction = parseFloat((existing.deduction + deduction).toFixed(2));
                            } else {
                              acc.push({ month: curr.month, count: count, deduction: parseFloat(deduction.toFixed(2)) });
                            }
                            return acc;
                          }, [])}
                          margin={{ top: 20, right: 10, left: -20, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                          <XAxis
                            dataKey="month"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: '#64748b', fontWeight: 600 }}
                            dy={10}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: '#94a3b8' }}
                            dx={-10}
                          />
                          <Tooltip
                            cursor={{ fill: '#f1f5f9' }}
                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                            labelStyle={{ fontWeight: 'bold', color: '#0f172a', marginBottom: '8px' }}
                            itemStyle={{ fontWeight: 600, fontSize: '13px' }}
                          />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            iconType="square"
                            align="left"
                            wrapperStyle={{ paddingLeft: '20px', fontSize: '13px', fontWeight: 600, color: '#475569', marginTop: '10px' }}
                          />
                          <Bar
                            dataKey="count"
                            name="Incident Count"
                            fill="#3b82f6"
                            radius={[4, 4, 0, 0]}
                            barSize={20}
                            animationDuration={1000}
                          />
                          <Bar
                            dataKey="deduction"
                            name="Deduction"
                            fill="#f97316"
                            radius={[4, 4, 0, 0]}
                            barSize={20}
                            animationDuration={1000}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#94a3b8', fontSize: '13px' }}>
                        No deductions for the selected period.
                      </div>
                    )}
                  </div>
                </div>
              )
            )}

            {/* --- LETTERS TAB CONTENT --- */}
            {modalView === 'letters' && (() => {
              const netPoints = (parseFloat(lRating.marks_added || 0) - parseFloat(lRating.marks_deducted || 0)).toFixed(2);
              const starCount = Math.min(5, Math.max(0, Math.round((parseFloat(lRating.final_marks || 0) / parseFloat(lRating.default_marks || 10)) * 5)));

              return (
                <>
                  <div style={{ minHeight: '45vh' }}>
                    {/* Metrics Row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', padding: '0 10px' }}>

                      {/* Dept Rank */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                        <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px', color: '#0369a1', boxShadow: '0 4px 10px rgba(186,230,253,0.4)' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><line x1="12" y1="22" x2="12" y2="2"></line><line x1="8" y1="6" x2="8" y2="6"></line><line x1="8" y1="10" x2="8" y2="10"></line><line x1="8" y1="14" x2="8" y2="14"></line><line x1="8" y1="18" x2="8" y2="18"></line><line x1="16" y1="6" x2="16" y2="6"></line><line x1="16" y1="10" x2="16" y2="10"></line><line x1="16" y1="14" x2="16" y2="14"></line><line x1="16" y1="18" x2="16" y2="18"></line></svg>
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: '2px 0' }}>{getOrdinal(letterData.department_rank || 0)}</div>
                        <div style={{ fontSize: '14px', color: '#0f172a', fontWeight: '600' }}>Department Rank</div>
                        <div style={{ fontSize: '11px', color: '#475569', textAlign: 'center' }}>({letterData.department_name || '-'})</div>
                      </div>

                      {/* Final Score */}
                      <div style={{ position: 'relative', width: '130px', height: '130px', flexShrink: 0, margin: '0 20px' }}>
                        {/* <div style={{ position: 'relative', width: '130px', height: '130px' }}> */}
                        <div style={{ position: 'absolute', top: '10px', left: '10px', right: '10px', bottom: '10px', borderRadius: '50%', boxShadow: '0 4px 14px rgba(0,0,0,0.08)', background: '#ffffff', zIndex: 0 }}></div>
                        <svg width="130" height="130" viewBox="0 0 130 130" style={{ position: 'relative', zIndex: 1 }}>
                          <circle cx="65" cy="65" r="54" fill="none" stroke="#f1f5f9" strokeWidth="8" />
                          <circle cx="65" cy="65" r="54" fill="none" stroke="#eab308" strokeWidth="8"
                            strokeLinecap="round"
                            strokeDasharray="339.29"
                            strokeDashoffset={339.29 - ((parseFloat(lRating.final_marks) / parseFloat(lConfig.total_marks)) * 339.29)}
                            transform="rotate(-90 65 65)"
                          />
                        </svg>
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                          <div style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', lineHeight: '1' }}>{lRating.final_marks}</div>
                          <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginTop: '2px', marginBottom: '4px' }}>/ {parseInt(lConfig.total_marks)}</div>
                          <div style={{ fontSize: '10px', color: '#0f172a', fontWeight: '700', textTransform: 'uppercase' }}>Final Score</div>

                          {/* <div style={{ fontSize: '10px', color: '#0f172a', fontWeight: '700', textTransform: 'uppercase', marginTop: '4px' }}>Conduct Score</div> */}
                          {/* Stars */}
                          {/* <div style={{ display: 'flex', gap: '2px', marginTop: '6px' }}>
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill={i < starCount ? "#eab308" : "#cbd5e1"} stroke="none">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                      ))}
                    </div> */}
                        </div>
                        {/* </div> */}
                        {/* <div style={{ fontSize: '12px', color: '#475569', fontWeight: '500', marginTop: '12px' }}>Total Potential: {parseInt(lConfig.total_marks)}</div> */}
                      </div>

                      {/* Company Rank */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                        <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'linear-gradient(135deg, #ffedd5 0%, #fed7aa 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px', color: '#c2410c', boxShadow: '0 4px 10px rgba(254,215,170,0.4)' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path><path d="M4 22h16"></path><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path></svg>
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: '2px 0' }}>{getOrdinal(letterData.sub_company_rank || 0)}</div>
                        <div style={{ fontSize: '14px', color: '#0f172a', fontWeight: '600' }}>Company Rank</div>
                        <div style={{ fontSize: '11px', color: '#475569', textAlign: 'center' }}>({letterData.sub_company_name || '-'})</div>
                      </div>

                    </div>

                    <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '0 0 20px 0' }} />

                    {/* Letter List Breakdown */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b' }}>Letters & Score Details</span>
                        <div style={{ background: parseFloat(netPoints) < 0 ? '#dc2626' : '#16a34a', color: '#ffffff', padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: parseFloat(netPoints) < 0 ? '0 2px 6px rgba(220,38,38,0.3)' : '0 2px 6px rgba(22,163,74,0.3)' }}>
                          Net Points: {netPoints}
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                        </div>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', paddingRight: '8px', maxHeight: '170px', overflowY: 'auto' }}>

                        {/* Mapped Letters */}
                        {lettersList.map((ltr, idx) => {
                          const isNegative = parseFloat(ltr.marks_delta) < 0;
                          return (
                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{ width: '30px', height: '30px', borderRadius: '14px', backgroundColor: isNegative ? '#fef08a' : '#bbf7d0', color: isNegative ? '#854d0e' : '#166534', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {isNegative ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                                  ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="7"></circle><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline></svg>
                                  )}
                                </div>
                                <div>
                                  <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '14px' }}>{ltr.issued_by ? ltr.issued_by.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'Letter'}</div>
                                  <div style={{ fontSize: '13px', color: '#475569', marginTop: '2px' }}>
                                    {ltr.letter_name} (Issued {new Date(ltr.issued_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})
                                  </div>
                                </div>
                              </div>
                              <div style={{ fontWeight: '600', color: isNegative ? '#991b1b' : '#0f172a', fontSize: '14px' }}>
                                {ltr.marks_delta}
                              </div>
                            </div>
                          );
                        })}

                        {/* Fallback for Appreciation (if none exist) */}
                        {!lettersList.some(l => l.letter_type?.toLowerCase().includes('appreciation')) && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                              <div style={{ width: '34px', height: '34px', borderRadius: '14px', backgroundColor: '#bbf7d0', color: '#166534', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="7"></circle><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline></svg>
                              </div>
                              <div>
                                <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '14px' }}>Appreciation Letters</div>
                                <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px' }}>No letters issued.</div>
                              </div>
                            </div>
                            <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '14px' }}>0.00</div>
                          </div>
                        )}

                        {/* Fallback for Warnings (if none exist) */}
                        {!lettersList.some(l => l.letter_type?.toLowerCase().includes('warning')) && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                              <div style={{ width: '34px', height: '34px', borderRadius: '14px', backgroundColor: '#fef08a', color: '#854d0e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                              </div>
                              <div>
                                <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '14px' }}>Warning Letters</div>
                                <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px' }}>No letters issued.</div>
                              </div>
                            </div>
                            <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '14px' }}>0.00</div>
                          </div>
                        )}

                        {/* Standard Conduct */}
                        {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '14px', backgroundColor: '#bae6fd', color: '#0369a1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0l-.77.78-.77-.78a5.4 5.4 0 0 0-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z"></path></svg>
                    </div>
                    <div>
                      <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '15px' }}>Standard Conduct</div>
                      <div style={{ fontSize: '13px', color: '#475569', marginTop: '2px' }}>Points Added / Deducted</div>
                    </div>
                  </div>
                  <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '18px' }}>
                    {lRating.default_marks || "10.00"}
                  </div>
                </div> */}

                      </div>
                    </div>
                  </div>
                </>
              );
            })()}

          </div>
        </div>
      )}

      {/* --- COMBINED TAB CONTENT --- */}
      {/* {activeTab === 'combined' && (() => {
        const empOverall = overallApiData?.employees?.[0] || {};
        const combinedScore = parseFloat(empOverall.overall_final_marks || 0).toFixed(2);
        const combinedTotal = parseFloat(overallApiData?.overall_total_marks || 30);
        const deptRank = empOverall.department_rank || 0;
        const compRank = empOverall.sub_company_rank || 0;
        const attScore = parseFloat(attendanceRating.final_marks || 0);
        const lttScore = parseFloat(lRating.final_marks || 0);
        const attTotal = parseFloat(attendanceRating.total_marks || 10);
        const lttTotal = parseFloat(lConfig.total_marks || 20);
        const percentage = (combinedScore / combinedTotal) * 100;

        const combinedStars = Math.min(5, Math.max(0, Math.round((parseFloat(combinedScore) / combinedTotal) * 5)));
        let ratingText = "Needs Improvement";
        if (percentage >= 90) ratingText = "Excellent";
        else if (percentage >= 75) ratingText = "Very Good";
        else if (percentage >= 50) ratingText = "Good";
        else if (percentage >= 35) ratingText = "Average";

        const totalDeductions = parseFloat(attendanceRating.total_deduction || 0) + parseFloat(lRating.marks_deducted || 0);
        const totalAdditions = parseFloat(lRating.marks_added || 0);
        const netImpact = (totalAdditions - totalDeductions).toFixed(2);

        let impactBg = '#f1f5f9';
        let impactColor = '#475569';
        let impactIcon = <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>;

        if (parseFloat(netImpact) > 0) {
          impactBg = '#dcfce7';
          impactColor = '#16a34a';
          impactIcon = <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"></line><polyline points="5 12 12 5 19 12"></polyline></svg>;
        } else if (parseFloat(netImpact) < 0) {
          impactBg = '#fee2e2';
          impactColor = '#dc2626';
          impactIcon = <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><polyline points="19 12 12 19 5 12"></polyline></svg>;
        }

        const attStars = Math.min(5, Math.max(0, Math.round((attScore / attTotal) * 5)));
        const attReliability = ((attScore / attTotal) * 100).toFixed(0);
        const lttStars = Math.min(5, Math.max(0, Math.round((lttScore / lttTotal) * 5)));
        const lttDiscipline = ((lttScore / lttTotal) * 100).toFixed(0);

        return (
          // <div style={{ minHeight: '450px' }}>
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden' }}>

            <div className="sar-star-performance__header">
              OVERALL STAR PERFORMANCE
            </div>

            <div className="sar-star-performance__body">
              <div className="sar-star-performance__slot">
                <div className="sar-star-performance__icon-circle" style={{ background: impactBg, color: impactColor }}>
                  {impactIcon}
                </div>
                <div className="sar-star-performance__impact-value">{parseFloat(netImpact) > 0 ? '+' : ''}{netImpact}</div>
                <div className="sar-star-performance__impact-label" style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                  <CustomTooltip tooltipContent={<div style={{ padding: '4px 8px', fontSize: '12px', lineHeight: '1.4', maxWidth: '200px' }}>Net Impact shows the difference between your bonus points (Appreciations) and deducted points (Warnings, Absences, Lates). A positive value means your good conduct outweighed any deductions.</div>} position="top">
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help' }}>Net Impact <InfoIcon size="12" color="#94a3b8" /></span>
                  </CustomTooltip>
                </div>
                <div className="sar-star-performance__badge sar-star-performance__badge--dept">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><line x1="12" y1="22" x2="12" y2="2"></line><line x1="8" y1="6" x2="8" y2="6"></line><line x1="8" y1="10" x2="8" y2="10"></line><line x1="8" y1="14" x2="8" y2="14"></line><line x1="8" y1="18" x2="8" y2="18"></line><line x1="16" y1="6" x2="16" y2="6"></line><line x1="16" y1="10" x2="16" y2="10"></line><line x1="16" y1="14" x2="16" y2="14"></line><line x1="16" y1="18" x2="16" y2="18"></line></svg>
                  <span>Department Rank:</span> <span className="sar-star-performance__badge-value"> {getOrdinal(deptRank)}</span>
                </div>
              </div>

              <div className="sar-star-performance__ring-wrap">
                <div className="sar-star-performance__ring-bg"></div>
                <svg width="150" height="150" viewBox="0 0 140 140" className="sar-star-performance__ring-svg">
                  <defs>
                    <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#8b5cf6" />
                    </linearGradient>
                  </defs>
                  <circle cx="70" cy="70" r="58" fill="none" stroke="#f1f5f9" strokeWidth="10" />
                  <circle cx="70" cy="70" r="58" fill="none" stroke="url(#ringGrad)" strokeWidth="10"
                    strokeLinecap="round"
                    strokeDasharray="364.4"
                    strokeDashoffset={364.4 - ((percentage / 100) * 364.4)}
                    transform="rotate(-90 70 70)"
                  />
                </svg>
                <div className="sar-star-performance__ring-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="url(#ringGrad)" stroke="none" className="sar-star-performance__ring-star">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                  <div className="sar-star-performance__score">{combinedScore}</div>
                  <div className="sar-star-performance__score-total">/ {combinedTotal}</div>
                  <div className="sar-star-performance__score-label" style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                    <CustomTooltip tooltipContent={<div style={{ padding: '4px 8px', fontSize: '12px', lineHeight: '1.4', maxWidth: '200px' }}>Your total combined score for both Attendance (10) and Letters (20) after all additions and deductions.</div>} position="bottom">
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help' }}>Final Score <InfoIcon size="12" color="#94a3b8" /></span>
                    </CustomTooltip>
                  </div>
                </div>

              </div>

              <div className="sar-star-performance__slot">
                <div className="sar-star-performance__icon-circle sar-star-performance__icon-circle--rating">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path><path d="M4 22h16"></path><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path></svg>
                </div>
                <div className="sar-star-performance__stars">
                  {renderStars(combinedScore, combinedTotal, 'overall', 16)}
                </div>
                <div className="sar-star-performance__rating-label" style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                  <CustomTooltip tooltipContent={<div style={{ padding: '4px 8px', fontSize: '12px', lineHeight: '1.4', maxWidth: '200px' }}>Your overall performance grade (Excellent, Good, etc.) based on your final score percentage.</div>} position="top">
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help' }}>Overall Rating: <InfoIcon size="12" color="#94a3b8" /></span>
                  </CustomTooltip>
                </div>
                <div className="sar-star-performance__rating-text">{ratingText}</div>
                <div className="sar-star-performance__badge sar-star-performance__badge--company">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path><path d="M4 22h16"></path><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path></svg>
                  <span>Company Rank:</span> <span className="sar-star-performance__badge-value">{getOrdinal(compRank)}</span>
                </div>
              </div>
            </div>


            <div style={{ background: '#f1f5f9', padding: '10px 20px', fontWeight: '800', color: '#0f172a', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
              RATING FACTOR BREAKDOWN
            </div>

            <div className="sar-star-performance__cards_breakdown">
              <div
                onClick={() => setModalView('attendance')}
                style={{ flex: 1, background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0', cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: '#ffedd5', color: '#ea580c', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                    </div>
                    <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '14px', lineHeight: '1.2' }}>Attendance<br />Factor</div>
                  </div>
                  {renderStars(attScore, attTotal, 'att-factor', 12)}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                  <span style={{ color: '#475569', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <CustomTooltip tooltipContent={<div style={{ padding: '4px 8px', fontSize: '12px', lineHeight: '1.4', maxWidth: '200px' }}>Total marks deducted due to late arrivals, half-days, or unapproved absences.</div>} position="top">
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help' }}>Deductions: <InfoIcon size="12" color="#94a3b8" /></span>
                    </CustomTooltip>
                  </span>
                  <span style={{ fontWeight: '800', color: '#0f172a' }}>-{attendanceRating.total_deduction || "0.00"}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                  <span style={{ color: '#475569', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <CustomTooltip tooltipContent={<div style={{ padding: '4px 8px', fontSize: '12px', lineHeight: '1.4', maxWidth: '200px' }}>Percentage based on your final attendance score (out of 10).</div>} position="top">
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help' }}>Reliability Score: <InfoIcon size="12" color="#94a3b8" /></span>
                    </CustomTooltip>
                  </span>
                  <span style={{ fontWeight: '800', color: '#0f172a' }}>{attReliability}%</span>
                </div>
              </div>

              <div
                onClick={() => setModalView('letters')}
                style={{ flex: 1, background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0', cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: '#f3e8ff', color: '#9333ea', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                    </div>
                    <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '14px', lineHeight: '1.2' }}>Letter<br />Factor</div>
                  </div>
                  {renderStars(lttScore, lttTotal, 'letter-factor', 12)}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                  <span style={{ color: '#475569', display: 'flex', alignItems: 'center', gap: '4px' }}><CustomTooltip tooltipContent={<div style={{ padding: '4px 8px', fontSize: '12px', lineHeight: '1.4', maxWidth: '200px' }}>Your final score (out of 20) after adding appreciations and subtracting warnings.</div>} position="top"><span style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help' }}>Additions/Score: <InfoIcon size="12" color="#94a3b8" /></span></CustomTooltip></span>
                  <span style={{ fontWeight: '800', color: '#0f172a' }}>{lttScore.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                  <span style={{ color: '#475569', display: 'flex', alignItems: 'center', gap: '4px' }}><CustomTooltip tooltipContent={<div style={{ padding: '4px 8px', fontSize: '12px', lineHeight: '1.4', maxWidth: '200px' }}>Percentage based on your final conduct and discipline score (out of 20).</div>} position="top"><span style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help' }}>Discipline Rating: <InfoIcon size="12" color="#94a3b8" /></span></CustomTooltip></span>
                  <span style={{ fontWeight: '800', color: '#0f172a' }}>{lttDiscipline}%</span>
                </div>
              </div>
            </div>

          </div>
          // </div>
        );
      })()} */}

      {/* --- COMBINED TAB CONTENT (responsive) --- */}
      {activeTab === 'combined' && (() => {
        const empOverall = overallApiData?.employees?.[0] || {};
        const combinedScore = parseFloat(empOverall.overall_final_marks || 0);
        const combinedTotal = parseFloat(overallApiData?.overall_total_marks || 30);
        const compRank = empOverall.sub_company_rank || 0;
        const companyName = data.sub_company_name && data.sub_company_name !== '-' ? data.sub_company_name : 'your company';

        const attScore = Math.max(0, parseFloat(attendanceRating.final_marks || 0));
        const attTotal = parseFloat(attendanceRating.total_marks || 10);
        const lttScore = Math.max(0, parseFloat(lRating.final_marks || 0));
        const lttTotal = parseFloat(lConfig.total_marks || 20);

        const pct = (v, t) => (t > 0 ? Math.min(100, Math.max(0, (v / t) * 100)) : 0);
        const overallPct = pct(combinedScore, combinedTotal);
        const attPct = pct(attScore, attTotal);
        const lttPct = pct(lttScore, lttTotal);
        const attShare = pct(attScore, combinedTotal);
        const lttShare = pct(lttScore, combinedTotal);

        const CIRC = 364.42; // 2 * PI * 58

        const tone = (p) =>
          p >= 75 ? { bar: '#10b981', bg: '#dcfce7', fg: '#15803d' }
            : p >= 50 ? { bar: '#f59e0b', bg: '#fef3c7', fg: '#b45309' }
              : { bar: '#ef4444', bg: '#fee2e2', fg: '#b91c1c' };

        const tip = (text) => (
          <div style={{ padding: '4px 8px', fontSize: '12px', lineHeight: '1.4', maxWidth: '200px' }}>{text}</div>
        );

        const totalDeducted = parseFloat(attendanceRating.total_deduction || 0);
        const lettersAdded = parseFloat(lRating.marks_added || 0);
        const lettersDeducted = parseFloat(lRating.marks_deducted || 0);

        const onKey = (fn) => (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); }
        };

        const factorCard = ({ onClick, iconBg, iconColor, icon, title, stars, score, total, pctVal, pctLabel, stats }) => {
          const t = tone(pctVal);
          return (
            <div role="button" tabIndex={0} aria-label={`${title} details`} className="sp-card" onClick={onClick} onKeyDown={onKey(onClick)}>

              <div className="sp-card__header">
                <div className="sp-card__title-wrap">
                  <div className="sp-card__icon" style={{ background: iconBg, color: iconColor }}>{icon}</div>
                  <div className="sp-card__title">{title}</div>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
              </div>

              <div className="sp-card__score-row">
                <div className="sp-card__score">
                  <b>{score.toFixed(2)}</b><span>/ {total}</span>
                </div>
                {/* <div className="sp-chip" style={{ background: t.bg, color: t.fg }}>
                  {pctVal.toFixed(0)}% {pctLabel}
                </div> */}
              </div>

              <div className="sp-card__stars">
                {stars}
              </div>

              <div className="sp-stats">
                {stats.map((s) => (
                  <div key={s.label} className="sp-stat">
                    <div className="sp-stat__value" style={s.color ? { color: s.color } : undefined}>{s.value}</div>
                    <div className="sp-stat__label">{s.label}</div>
                  </div>
                ))}
              </div>

            </div>
          );
        };

        return (
          <div className="sp-root">

            {/* ===== HERO ===== */}
            <div className="sp-hero">

              <div className="sp-ring">
                <div className="sp-ring__disc" />
                <svg viewBox="0 0 140 140" className="sp-ring__svg">
                  <defs>
                    <linearGradient id="spRingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#8b5cf6" />
                    </linearGradient>
                  </defs>
                  <circle cx="70" cy="70" r="58" fill="none" stroke="#e8edf5" strokeWidth="10" />
                  <circle cx="70" cy="70" r="58" fill="none" stroke="url(#spRingGrad)" strokeWidth="10"
                    strokeLinecap="round" strokeDasharray={CIRC}
                    strokeDashoffset={CIRC - (overallPct / 100) * CIRC}
                    transform="rotate(-90 70 70)" />
                </svg>
                <div className="sp-ring__center">
                  <div className="sp-ring__score">{combinedScore.toFixed(2)}</div>
                  <div className="sp-ring__total">/ {combinedTotal}</div>
                  <CustomTooltip tooltipContent={tip('Your combined score for Attendance (10) and Letters (20) after all additions and deductions.')} position="bottom">
                    <span className="sp-ring__label">Final score <InfoIcon size="12" color="#94a3b8" /></span>
                  </CustomTooltip>
                </div>
              </div>

              <div className="sp-aside">

                {/* Company rank: opens the Star Ranking tab */}
                <div role="button" tabIndex={0} aria-label="View company ranking" className="sp-rank" onClick={goToRanking} onKeyDown={onKey(goToRanking)}>
                  <div className="sp-rank__icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path><path d="M4 22h16"></path><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path></svg>
                  </div>
                  <div className="sp-rank__body">
                    <div className="sp-rank__label">Company rank</div>
                    <div className="sp-rank__line">
                      <span className="sp-rank__value">{getOrdinal(compRank)}</span>
                      <span className="sp-rank__company">in {companyName}</span>
                    </div>
                  </div>
                  <div className="sp-rank__hint">
                    <span className="sp-rank__hint-text">See all rankings</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                  </div>
                </div>

                {/* Where the marks come from */}
                <div>
                  <div className="sp-split__title">Where your marks come from</div>
                  <div className="sp-split__bar">
                    <div className="sp-split__seg" style={{ width: `${attShare}%`, background: '#f97316' }} />
                    <div className="sp-split__seg" style={{ width: `${lttShare}%`, background: '#8b5cf6' }} />
                  </div>
                  <div className="sp-split__legend">
                    <span className="sp-split__item">
                      <i className="sp-dot" style={{ background: '#f97316' }} />
                      Attendance <b style={{ color: '#0f172a' }}>{attScore.toFixed(2)}</b>
                    </span>
                    <span className="sp-split__item">
                      <i className="sp-dot" style={{ background: '#8b5cf6' }} />
                      Letters <b style={{ color: '#0f172a' }}>{lttScore.toFixed(2)}</b>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== FACTOR CARDS ===== */}
            <div className="sp-cards">

              {factorCard({
                onClick: () => setModalView('attendance'),
                iconBg: '#ffedd5', iconColor: '#ea580c',
                icon: <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>,
                title: 'Attendance',
                stars: renderStars(attScore, attTotal, 'att-factor', 12),
                score: attScore, total: attTotal, pctVal: attPct, pctLabel: 'reliable',
                stats: [
                  { label: 'Absent', value: absent.count },
                  { label: 'Late', value: late.count },
                  { label: 'Half days', value: halfday.count },
                ],
              })}

              {factorCard({
                onClick: () => setModalView('letters'),
                iconBg: '#f3e8ff', iconColor: '#9333ea',
                icon: <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>,
                title: 'Letters',
                stars: renderStars(lttScore, lttTotal, 'letter-factor', 12),
                score: lttScore, total: lttTotal, pctVal: lttPct, pctLabel: 'discipline',
                stats: [
                  { label: 'Issued', value: lRating.letter_count ?? lettersList.length },
                  { label: 'Added', value: `+${lettersAdded.toFixed(2)}`, color: lettersAdded > 0 ? '#15803d' : '#0f172a' },
                  { label: 'Deducted', value: `-${lettersDeducted.toFixed(2)}`, color: lettersDeducted > 0 ? '#b91c1c' : '#0f172a' },
                ],
              })}

            </div>
          </div>
        );
      })()}


    </div >
  );
};

export default AppraisalMonthlyTracker;






